import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdSearch, MdFilterList, MdImage, MdOutlineRefresh } from 'react-icons/md';
import api from '../../services/api';
import { getImageUrl } from '../../lib/utils';

const BulkOrdersPage = () => {
  const [bulkOrders, setBulkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ current: 1, total: 1 });

  const fetchBulkOrders = async (page = 1) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({ page, limit: 10 });
      if (statusFilter) queryParams.append('status', statusFilter);

      const response = await api.get(`/bulk-orders?${queryParams.toString()}`);
      setBulkOrders(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching bulk orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBulkOrders(1);
  }, [statusFilter]);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusMap[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">
            Bulk Image Orders
          </h1>
          <p className="mt-1 text-sm text-admin-slate-600 dark:text-admin-slate-400">
            Manage customer handwritten or printed order lists
          </p>
        </div>
      </div>

      {/* Filters Area */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-64">
            <MdSearch className="h-5 w-5 text-gray-400 mr-2" />
            <input 
              type="text" 
              placeholder="Search (Coming soon)" 
              disabled
              className="bg-transparent border-none outline-none text-sm w-full cursor-not-allowed text-gray-400"
            />
          </div>

          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-r border-gray-200">
              <MdFilterList className="h-5 w-5 text-gray-500" />
            </div>
            <select 
              className="outline-none px-3 py-2 text-sm bg-white cursor-pointer"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        
        <button 
          onClick={() => fetchBulkOrders(pagination.current)}
          className="flex items-center text-gray-600 hover:text-admin-ucla-600 transition-colors bg-gray-50 hover:bg-admin-ucla-50 px-3 py-2 rounded-lg"
        >
          <MdOutlineRefresh className={`h-5 w-5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <th className="px-6 py-4 font-semibold border-b border-gray-100">Order ID / Image</th>
                <th className="px-6 py-4 font-semibold border-b border-gray-100">Customer</th>
                <th className="px-6 py-4 font-semibold border-b border-gray-100">Date Submitted</th>
                <th className="px-6 py-4 font-semibold border-b border-gray-100">Status</th>
                <th className="px-6 py-4 font-semibold border-b border-gray-100 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-600 mx-auto mb-4"></div>
                    Loading orders...
                  </td>
                </tr>
              ) : bulkOrders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <MdImage className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>No bulk orders found.</p>
                  </td>
                </tr>
              ) : (
                bulkOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-12 w-12 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden border border-gray-200">
                          <img src={getImageUrl(order.imagePath)} alt="Order list" className="h-full w-full object-cover" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]" title={order._id}>
                            #{order._id.substring(order._id.length - 6)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {order.user ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.user.name}</div>
                          <div className="text-xs text-gray-500">{order.user.email}</div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-gray-900">Guest</div>
                          <div className="text-xs text-gray-500">{order.guestContactInfo}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link 
                        to={`/admin/bulk-orders/${order._id}`}
                        className="text-admin-ucla-600 hover:text-admin-ucla-800 font-medium text-sm"
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && pagination.total > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-100">
            <span className="text-sm text-gray-500">
              Page {pagination.current} of {pagination.total}
            </span>
            <div className="flex space-x-2">
              <button 
                disabled={pagination.current === 1}
                onClick={() => fetchBulkOrders(pagination.current - 1)}
                className={`px-3 py-1 text-sm rounded ${pagination.current === 1 ? 'text-gray-400 bg-gray-100' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'}`}
              >
                Previous
              </button>
              <button 
                disabled={pagination.current === pagination.total}
                onClick={() => fetchBulkOrders(pagination.current + 1)}
                className={`px-3 py-1 text-sm rounded ${pagination.current === pagination.total ? 'text-gray-400 bg-gray-100' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'}`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkOrdersPage;
